import { Component, ElementRef, Input, OnDestroy, OnInit, ViewContainerRef, inject,
    ChangeDetectionStrategy
} from "@angular/core";
import { ActivatedRoute } from "@angular/router";
import { Observable } from "rxjs";
import { CanComponentDeactivate } from "src/modules/sm-base/models/can-component-deactivate.model";
import { SmTimer } from "src/modules/sm-base/models/sm-timer.model";
import { ObjectOfSimpleDataTypes } from "src/modules/utils/shared/object-of-simple-data-types.model";
import { OrdinaryObject } from "src/modules/utils/shared/ordinary-object.model";
import { Utils } from "src/modules/utils/shared/utils";
import { MainAppService } from "../services/main-app.service";
import { GuiUtils } from "src/modules/utils/misc/gui-utils";
import { Arrays } from "src/modules/utils/shared/arrays";
import { Dates } from "src/modules/utils/shared/dates";
import { Strings } from "src/modules/utils/shared/strings";
import { Objects } from "src/modules/utils/shared/objects";
import { ButtonDefinition } from "src/modules/sm-base/models/button-definition.model";
import { ContainerDialogComponent } from "src/modules/sm-base/components/container-dialog/container-dialog.component";
import { ViewState } from "src/modules/sm-base/models/view-state.model";
import { Subscription } from "src/modules/sm-base/models/subscription.model";
import { MenuItem } from "@openng/optimus-ui/api";

@Component({
    selector: 'component-view',
    template: "",
    changeDetection: ChangeDetectionStrategy.Eager,
    standalone: false
})
export abstract class ComponentView implements OnInit, OnDestroy, CanComponentDeactivate {

    _Arrays = Arrays;
    _Dates = Dates;
    _GuiUtils = GuiUtils;
    _Objects = Objects;
    _Strings = Strings;
    _Utils = Utils;

    app = inject(MainAppService);
    route = inject(ActivatedRoute);
    viewContainerRef = inject(ViewContainerRef);
    elementRef = inject(ElementRef);

    @Input()
    embeddedComponent = false;

    containerDialog: ContainerDialogComponent;

    neededParams: ObjectOfSimpleDataTypes = {};
    useQueryParams = false;
    inited = false;
    skipCloseCheck = false;

    fullScreenComponent = true;

    viewState = this.getInitialViewState();

    timers: SmTimer[] = [];
    subscriptions: Subscription[] = [];
    keyboardListenerGuids: string[] = [];

    runningButtonClicks = new Set<string>();

    ngOnInit(): void {
        if (this.route != null) {
            if (this.useQueryParams) {
                this.app.subscribeToQueryParamsChanges(this.route, this.initParamsInternal.bind(this));
            }
            else {
                this.app.subscribeToParamsChanges(this.route, this.initParamsInternal.bind(this));
            }
        }
    }

    ngOnDestroy(): void {
        this.destroy();
        this.clearTimers();
        this.clearSubscriptions();
        for (let keyboardListenerGuid of this.keyboardListenerGuids) {
            this.app.removeKeyboardListener(keyboardListenerGuid);
        }
    }

    canDeactivate(): Observable<boolean> | Promise<boolean> | boolean {
        if (this.skipCloseCheck) {
            this.skipCloseCheck = false;
            return true;
        }
        let message = this.getCanDeactivateMessage();
        return message == null || window.confirm(message == "" ? "Wenn Sie fortfahren, gehen alle Änderungen verloren" : message);
    }

    getCanDeactivateMessage(): string {
        return null;
    }

    getInitialViewState(): ViewState {
        return new ViewState();
    }

    addSubscription(subscription: Subscription): void {
        this.subscriptions.push(subscription);
    }

    addTimer(timer: SmTimer, onlyIfVisible = false): SmTimer {
        if (onlyIfVisible) {
            let oldTimer = timer;
            timer = new SmTimer(timer.name, timer.initial, timer.period, async () => {
                if (GuiUtils.isComponentVisible(this.elementRef)) {
                    await oldTimer.onTimerInternal();
                }
            });
            oldTimer.destroy();
        }

        this.timers.push(timer);
        return timer;
    }

    removeTimer(timer: SmTimer): void {
        let index = this.timers.indexOf(timer);
        if (index > 0) {
            this.timers[index].destroy();
            this.timers = Arrays.withoutIndex(this.timers, index);
        }
    }

    clearTimers(): void {
        for (let timer of this.timers) {
            timer.destroy();
        }
        this.timers = [];
    }

    clearSubscriptions(): void {
        for (let subscription of this.subscriptions) {
            subscription.unsubscribe();
        }
        this.subscriptions = [];    
    }

    addKeyboardListener(key: string, callback: (KeyboardEvent) => void): void {
        this.keyboardListenerGuids = [...this.keyboardListenerGuids, this.app.addKeyboardListener(key, event2 => {
            callback(event2);
        })];
    }

    async buttonClick(id: string, action: () => Promise<any>): Promise<void> {
        if (this.runningButtonClicks.has(id)) {
            console.log("Doppelter ButtonClick erkannt. Click wird ignoriert");
            return;
        }
        this.runningButtonClicks.add(id);
        try {
            await action();
        }
        finally {
            this.runningButtonClicks.delete(id);
        }
    }

    async initParamsInternal(params: OrdinaryObject): Promise<void> {
        this.inited = false;
        if (!this.embeddedComponent) {
            let p = Utils.parseRequestParams(params, this.neededParams);
            for (let paramName of Objects.getPropertyNames(this.neededParams)) {
                this[paramName] = p[paramName];
            }
            if (await this.initParams()) {
                GuiUtils.angularTimer(() => {
                    this.inited = true;
                });
            }
        } 
    }

    async initEmbedded(): Promise<void> {
        if (!this.inited && await this.initParams()) {
            GuiUtils.angularTimer(() => {
                this.inited = true;
            });
        }
    }

    async initParams(): Promise<boolean> {
        return Promise.resolve(true);
    }

    destroy(): void {
    }

    async close(): Promise<void> {
        if (this.embeddedComponent) {
            if (this.containerDialog != null) {
                this.containerDialog.closeDialog();
            }
        }
        else {
            this.app.navigateBack();
        }
        return Promise.resolve();
    }

    getDialogButtons(): ButtonDefinition[] {
        return null;
    }

    async buttonClickedFromExtern(_id: string): Promise<void> {
    }

    updateNavigation(pageTitle: string, breadcrumbsItems: MenuItem[]): void {
        if (!this.embeddedComponent) {
            this.app.updateNavigation(pageTitle, breadcrumbsItems);
        }
    }

}
